/* Содержимое, которое приходит позже первого кадра блока: портреты витрины главной (через BFF, секунды) и обложки
   плиток из сети. У эталона его не бывает — картинки там blob в памяти, и блок входит уже целым. Здесь место под
   таким содержимым стоит с первого кадра, а само оно проявляется (fadeIn), а не возникает. Пришедшее к первому
   кадру показывается сразу, как часть входа блока. */
import { useEffect, useLayoutEffect, useRef, type HTMLAttributes, type ImgHTMLAttributes } from 'react';
import { fadeIn } from './motion';

/** Блок с поздним содержимым: ready стало true после его первого кадра — блок проявляется */
export function LateFade({ as = 'div', ready, ...props }: { as?: 'div' | 'span'; ready: boolean } & HTMLAttributes<HTMLElement>) {
  const ref = useRef<HTMLDivElement & HTMLSpanElement>(null);
  const painted = useRef(false);
  const was = useRef(ready);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      painted.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, []);
  useLayoutEffect(() => {
    if (ready && !was.current && painted.current) fadeIn(ref.current);
    was.current = ready;
  }, [ready]);
  return as === 'span' ? <span ref={ref} {...props} /> : <div ref={ref} {...props} />;
}

/** Картинка, не готовая к первому кадру после своего адреса, по загрузке проявляется; готовая — сразу.
    Загруженная несёт data-ld: по нему CSS снимает скелетон места (пульс .pic, .ic.th), пока картинки нет */
export function FadeImg({ onLoad, ...props }: ImgHTMLAttributes<HTMLImageElement>) {
  const ref = useRef<HTMLImageElement>(null);
  const late = useRef(false);
  const src = props.src;
  useLayoutEffect(() => {
    late.current = false;
    const img = ref.current;
    img?.toggleAttribute('data-ld', !!img.complete && img.naturalWidth > 0);
    const frame = requestAnimationFrame(() => {
      const img = ref.current;
      late.current = !!img && !(img.complete && img.naturalWidth > 0);
    });
    return () => cancelAnimationFrame(frame);
  }, [src]);
  return (
    <img
      ref={ref}
      {...props}
      onLoad={event => {
        event.currentTarget.setAttribute('data-ld', '');
        /* картинку собирает пиксельная сборка (.pxw, features/tts/pixel-reveal.ts — обложки «Всех шаблонов»): своё проявление поверх неё
           показывало квадратную картинку, пока у рамки снята подрезка, а по его концу .pxw снова гасила картинку — моргание и позднее скругление */
        if (late.current && !event.currentTarget.classList.contains('pxw')) fadeIn(event.currentTarget);
        late.current = false;
        onLoad?.(event);
      }}
    />
  );
}
